import type { TranslatedField } from 'admin-panel/constants/types';
import { splitPageHtmlPrefix } from 'admin-panel/utils/page-html-prefix';
import { sanitizeSourceHtmlForEmission } from 'admin-panel/utils/wysiwyg-editor/html';

export type PageHeaderImageBlock = {
  src: string;
  html: string;
  cropY: number | null;
  cropInvalid: boolean;
};

export type PageHeaderImageTranslations = Partial<
  Record<string, PageHeaderImageBlock>
>;

export type PageHeaderImagePrefixTranslations = Partial<Record<string, string>>;

export type ParsedPageHeaderImage = {
  headerImage: PageHeaderImageBlock | null;
  prefixHtml: string;
  bodyHtml: string;
};

const KNOWN_HEADER_INSTRUCTION =
  'bitte fügen sie das gewünschte header-bild in die nachfolgende tabellenzelle ein';

const NORMALIZED_PERCENTAGE_PATTERN = /^(?:(?:0|[1-9]\d?)(?:\.\d{1,2})?|100)$/;
const POSITIVE_PIXEL_HEIGHT_PATTERN = /^(\d+(?:\.\d+)?)px$/;
// The attribute marks Admin-owned crop styles; matching both values prevents
// external HTML edits from making Admin and Ted show different positions.
const HEADER_IMAGE_FOCUS_Y_ATTRIBUTE = 'data-header-image-focus-y';
const HEADER_IMAGE_FOCUS_X_ATTRIBUTE = 'data-header-image-focus-x';
const CROP_STYLE_PROPERTIES = [
  'width',
  'height',
  'object-fit',
  'object-position',
] as const;
const LEGACY_CROP_STYLE_PROPERTY = 'top';
const LEGACY_CROP_TRANSFORM = 'translateY(-50%)';
export const TED_PAGE_CONTENT_WIDTH = 880;
export const DEFAULT_HEADER_IMAGE_HEIGHT = 250;
export const MIN_HEADER_IMAGE_HEIGHT = 100;
export const MAX_HEADER_IMAGE_HEIGHT = 1000;

export function isValidHeaderImageHeight(height: number) {
  return (
    Number.isInteger(height) &&
    height >= MIN_HEADER_IMAGE_HEIGHT &&
    height <= MAX_HEADER_IMAGE_HEIGHT
  );
}

const ALLOWED_HEADER_ELEMENTS = new Set([
  'A',
  'BR',
  'H1',
  'IMG',
  'P',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
]);
const ALLOWED_HEADER_ATTRIBUTES = new Set([
  'align',
  'alt',
  'bgcolor',
  'border',
  'cellpadding',
  'cellspacing',
  'class',
  'colspan',
  'decoding',
  'dir',
  'height',
  'id',
  'lang',
  'loading',
  'rowspan',
  'scope',
  'sizes',
  'src',
  'srcset',
  'style',
  'summary',
  'title',
  'valign',
  'width',
]);
const ALLOWED_LINK_ATTRIBUTES = new Set(['href', 'rel', 'target', 'title']);

function normalizeText(value: string) {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[:.!]+$/, '');
}

function isSafeImageSource(source: string | null): source is string {
  if (!source) return false;

  try {
    const url = new URL(source);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function normalizeCropValue(value: number) {
  return Number(value.toFixed(2));
}

function normalizeCropY(cropY: number) {
  if (!Number.isFinite(cropY) || cropY < 0 || cropY > 100) {
    return null;
  }

  return normalizeCropValue(cropY);
}

function parsePercentage(value: string | null) {
  if (!value || !NORMALIZED_PERCENTAGE_PATTERN.test(value)) return null;

  return Number(value);
}

function parseCropStylePosition(value: string) {
  const [xValue, yValue, ...remainder] = value.trim().split(/\s+/);
  if (!xValue || !yValue || remainder.length > 0) return null;

  const x = parsePercentage(xValue.endsWith('%') ? xValue.slice(0, -1) : null);
  const y = parsePercentage(yValue.endsWith('%') ? yValue.slice(0, -1) : null);

  return x === null || y === null ? null : { x, y };
}

function parseLegacyCropTop(image: HTMLImageElement) {
  const value = image.style.top;

  if (
    !value ||
    image.style.getPropertyPriority(LEGACY_CROP_STYLE_PROPERTY) !== '' ||
    !value.endsWith('%')
  ) {
    return null;
  }

  return parsePercentage(value.slice(0, -1));
}

function hasSupportedLegacyCropPosition(image: HTMLImageElement) {
  const legacyTop = parseLegacyCropTop(image);

  if (!image.style.top) return !image.style.transform;
  if (legacyTop === null) return false;

  return (
    !image.style.transform ||
    (image.style.transform === LEGACY_CROP_TRANSFORM &&
      image.style.getPropertyPriority('transform') === '')
  );
}

function parseOwnedCropStylePosition(image: HTMLImageElement) {
  const position = parseCropStylePosition(image.style.objectPosition);
  const pixelHeight = image.style.height.match(
    POSITIVE_PIXEL_HEIGHT_PATTERN
  )?.[1];
  const ownedStylesMatch =
    image.style.width === '100%' &&
    (image.style.height === '100%' ||
      (pixelHeight !== undefined && Number(pixelHeight) > 0)) &&
    image.style.objectFit === 'cover' &&
    CROP_STYLE_PROPERTIES.every(
      (property) => image.style.getPropertyPriority(property) === ''
    );

  return position && ownedStylesMatch ? position : null;
}

function parseHeaderImageCropY(image: HTMLImageElement) {
  const yValue = image.getAttribute(HEADER_IMAGE_FOCUS_Y_ATTRIBUTE);
  const position = parseOwnedCropStylePosition(image);

  if (image.hasAttribute(HEADER_IMAGE_FOCUS_X_ATTRIBUTE)) return false;
  if (yValue === null) return position ? false : null;

  const y = parsePercentage(yValue);

  if (
    y === null ||
    !position ||
    !hasSupportedLegacyCropPosition(image) ||
    position.x !== 50 ||
    position.y !== y
  ) {
    return false;
  }

  return y;
}

function isFullWidthImage(image: HTMLImageElement) {
  return (
    image.classList.contains('header') ||
    image.getAttribute('width')?.replace(/\s/g, '') === '100%' ||
    image.style.width.replace(/\s/g, '') === '100%'
  );
}

function meaningfulChildNodes(element: ParentNode) {
  return [...element.childNodes].filter((node) => {
    return !(
      node.nodeType === Node.COMMENT_NODE ||
      (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
    );
  });
}

function parseSingleElement(html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;

  const meaningfulNodes = meaningfulChildNodes(template.content);

  if (meaningfulNodes.length !== 1) return null;

  const [element] = meaningfulNodes;

  return element instanceof HTMLElement ? element : null;
}

function hasOnlyAllowedHeaderMarkup(element: HTMLElement) {
  return [element, ...element.querySelectorAll('*')].every((descendant) => {
    if (!ALLOWED_HEADER_ELEMENTS.has(descendant.tagName)) return false;
    const allowedAttributes =
      descendant.tagName === 'A'
        ? ALLOWED_LINK_ATTRIBUTES
        : ALLOWED_HEADER_ATTRIBUTES;

    return [...descendant.attributes].every(({ name }) => {
      const normalizedName = name.toLowerCase();

      return (
        allowedAttributes.has(normalizedName) ||
        normalizedName.startsWith('aria-') ||
        normalizedName.startsWith('data-')
      );
    });
  });
}

function isImageOnlyWrapper(element: HTMLElement, image: HTMLImageElement) {
  const meaningfulChildren = meaningfulChildNodes(element);

  if (meaningfulChildren.length !== 1) return false;
  if (meaningfulChildren[0] === image) return true;

  const child = meaningfulChildren[0];
  if (!(child instanceof HTMLAnchorElement)) return false;

  const anchorChildren = meaningfulChildNodes(child);

  return anchorChildren.length === 1 && anchorChildren[0] === image;
}

function isRecognizedHeaderTable(table: HTMLTableElement) {
  if (table.querySelectorAll('img').length !== 1) return false;
  if (table.querySelector('table, video, audio, iframe, object, embed')) {
    return false;
  }

  const textOnlyTable = table.cloneNode(true);
  if (!(textOnlyTable instanceof HTMLTableElement)) return false;

  textOnlyTable.querySelector('img')?.remove();

  const remainingText = normalizeText(textOnlyTable.textContent ?? '');
  const containsOnlyKnownInstruction =
    remainingText === '' || remainingText === KNOWN_HEADER_INSTRUCTION;

  if (!containsOnlyKnownInstruction) return false;

  return (
    table.getAttribute('summary')?.toLowerCase() === 'header' ||
    remainingText === KNOWN_HEADER_INSTRUCTION
  );
}

function recognizedHeaderImage(element: HTMLElement) {
  const imageCount =
    element instanceof HTMLImageElement
      ? 1
      : element.querySelectorAll('img').length;

  if (imageCount !== 1) return null;

  const image =
    element instanceof HTMLImageElement
      ? element
      : element.querySelector('img');

  if (!(image instanceof HTMLImageElement)) return null;
  const source = image.getAttribute('src');
  if (!isSafeImageSource(source)) return null;

  const links = element.querySelectorAll('a');
  if (links.length > 1 || (links[0] && !isImageOnlyWrapper(links[0], image))) {
    return null;
  }

  if (element instanceof HTMLTableElement) {
    return isRecognizedHeaderTable(element) ? image : null;
  }

  if (element instanceof HTMLImageElement) {
    return isFullWidthImage(image) ? image : null;
  }

  if (element.tagName !== 'P' && element.tagName !== 'H1') return null;

  return isFullWidthImage(image) && isImageOnlyWrapper(element, image)
    ? image
    : null;
}

function sanitizeRecognizedHeader(html: string) {
  const sanitizedHtml = sanitizeSourceHtmlForEmission(html);
  const element = parseSingleElement(sanitizedHtml);
  const image =
    element && hasOnlyAllowedHeaderMarkup(element)
      ? recognizedHeaderImage(element)
      : null;

  if (!element || !image) return null;

  const parsedCropY = parseHeaderImageCropY(image);
  let cropInvalid = parsedCropY === false;
  let cropY: number | null =
    typeof parsedCropY === 'number' ? parsedCropY : null;

  if (cropY !== null) {
    const cell = compatibleCropViewportCell(element, image);
    const imageHeight = image.style.height;

    if (
      !cell ||
      (imageHeight !== '100%' && imageHeight !== cell.style.height)
    ) {
      cropInvalid = true;
      cropY = null;
    }
  }

  image.removeAttribute('srcset');
  image.removeAttribute('sizes');

  return { element, image, html: element.outerHTML, cropY, cropInvalid };
}

function setCanonicalTableStyles(
  table: HTMLTableElement,
  cell: HTMLTableCellElement
) {
  table.setAttribute('summary', 'header');
  table.style.setProperty('width', '100%');
  cell.style.setProperty('height', `${DEFAULT_HEADER_IMAGE_HEIGHT}px`);
  cell.style.setProperty('overflow', 'hidden');
  cell.style.setProperty('display', 'inline-block');
  cell.style.setProperty('width', '100%');
}

function compatibleCropViewportCell(
  element: HTMLElement,
  image: HTMLImageElement
) {
  if (!(element instanceof HTMLTableElement)) return null;

  const cell = image.closest<HTMLTableCellElement>('td, th');
  const height = cell?.style.height.match(POSITIVE_PIXEL_HEIGHT_PATTERN)?.[1];

  if (
    !cell ||
    cell.closest('table') !== element ||
    element.getAttribute('summary')?.toLowerCase() !== 'header' ||
    element.querySelectorAll('tr').length !== 1 ||
    element.querySelectorAll('td, th').length !== 1 ||
    element.style.width !== '100%' ||
    height === undefined ||
    Number(height) <= 0 ||
    cell.style.overflow !== 'hidden' ||
    cell.style.display !== 'inline-block' ||
    cell.style.width !== '100%'
  ) {
    return null;
  }

  return cell;
}

function pageHeaderImageCropContext(headerImage: PageHeaderImageBlock) {
  const recognizedHeader = sanitizeRecognizedHeader(headerImage.html);
  const cell = recognizedHeader
    ? compatibleCropViewportCell(
        recognizedHeader.element,
        recognizedHeader.image
      )
    : null;

  if (
    !recognizedHeader ||
    recognizedHeader.cropInvalid ||
    !cell ||
    !isFullWidthImage(recognizedHeader.image) ||
    !hasSupportedLegacyCropPosition(recognizedHeader.image)
  ) {
    return null;
  }

  return { recognizedHeader, cell };
}

export function parsePageHeaderImage(html: string): ParsedPageHeaderImage {
  const { tokens, remainingHtml: content } = splitPageHtmlPrefix(html);
  const prefix = tokens.join('');
  const template = document.createElement('template');
  template.innerHTML = content;

  const candidate = template.content.firstChild;

  if (!(candidate instanceof HTMLElement)) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const recognizedHeader = sanitizeRecognizedHeader(candidate.outerHTML);

  if (!recognizedHeader) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const source = recognizedHeader.image.getAttribute('src');
  if (!source) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const serializedCandidate = candidate.outerHTML;
  candidate.remove();
  const bodyHtml = content.startsWith(serializedCandidate)
    ? content.slice(serializedCandidate.length)
    : template.innerHTML;

  return {
    headerImage: {
      src: source,
      html: recognizedHeader.html,
      cropY: recognizedHeader.cropY,
      cropInvalid: recognizedHeader.cropInvalid,
    },
    prefixHtml: prefix,
    bodyHtml,
  };
}

export function createPageHeaderImage(
  source: string
): PageHeaderImageBlock | null {
  if (!isSafeImageSource(source)) return null;

  const table = document.createElement('table');
  const body = document.createElement('tbody');
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  setCanonicalTableStyles(table, cell);

  const image = document.createElement('img');
  image.className = 'header';
  image.setAttribute('src', source);
  image.setAttribute('alt', '');
  image.setAttribute('width', '100%');

  cell.append(image);
  row.append(cell);
  body.append(row);
  table.append(body);

  return {
    src: source,
    html: table.outerHTML,
    cropY: null,
    cropInvalid: false,
  };
}

export function getPageHeaderImageLegacyTop(headerImage: PageHeaderImageBlock) {
  if (headerImage.cropY !== null) return null;

  const recognizedHeader = sanitizeRecognizedHeader(headerImage.html);
  if (
    !recognizedHeader ||
    recognizedHeader.cropInvalid ||
    recognizedHeader.cropY !== null
  ) {
    return null;
  }

  return parseLegacyCropTop(recognizedHeader.image);
}

export function getPageHeaderImageViewportHeight(
  headerImage: PageHeaderImageBlock
) {
  const recognizedHeader = sanitizeRecognizedHeader(headerImage.html);
  const cell = recognizedHeader
    ? compatibleCropViewportCell(
        recognizedHeader.element,
        recognizedHeader.image
      )
    : null;
  const height = cell?.style.height.match(POSITIVE_PIXEL_HEIGHT_PATTERN)?.[1];

  return height === undefined ? null : Number(height);
}

export function canCropPageHeaderImage(headerImage: PageHeaderImageBlock) {
  return pageHeaderImageCropContext(headerImage) !== null;
}

export function applyPageHeaderImageCrop(
  headerImage: PageHeaderImageBlock,
  cropY: number
): PageHeaderImageBlock | null {
  const normalizedCropY = normalizeCropY(cropY);
  if (normalizedCropY === null) return null;

  const context = pageHeaderImageCropContext(headerImage);
  if (!context) return null;

  const { recognizedHeader, cell } = context;
  const { element, image } = recognizedHeader;
  const y = String(normalizedCropY);

  image.setAttribute(HEADER_IMAGE_FOCUS_Y_ATTRIBUTE, y);
  if (parseLegacyCropTop(image) !== null) {
    image.style.removeProperty(LEGACY_CROP_STYLE_PROPERTY);
    if (image.style.transform === LEGACY_CROP_TRANSFORM) {
      image.style.removeProperty('transform');
    }
  }
  image.style.setProperty('width', '100%');
  image.style.setProperty('height', cell.style.height);
  image.style.setProperty('object-fit', 'cover');
  image.style.setProperty('object-position', `50% ${y}%`);

  return {
    src: recognizedHeader.image.getAttribute('src') ?? headerImage.src,
    html: element.outerHTML,
    cropY: normalizedCropY,
    cropInvalid: false,
  };
}

export function applyPageHeaderImageHeight(
  headerImage: PageHeaderImageBlock,
  height: number
): PageHeaderImageBlock | null {
  if (!isValidHeaderImageHeight(height)) return null;

  const context = pageHeaderImageCropContext(headerImage);
  if (!context) return null;

  const { recognizedHeader, cell } = context;
  const { element, image } = recognizedHeader;
  const pixelHeight = `${height}px`;
  const imageHeightFollowsViewport =
    recognizedHeader.cropY !== null || image.style.height === cell.style.height;

  cell.style.setProperty('height', pixelHeight);
  if (imageHeightFollowsViewport) {
    image.style.setProperty('height', pixelHeight);
  }

  return {
    src: image.getAttribute('src') ?? headerImage.src,
    html: element.outerHTML,
    cropY: recognizedHeader.cropY,
    cropInvalid: false,
  };
}

export function replacePageHeaderImageSource(
  headerImage: PageHeaderImageBlock,
  source: string
): PageHeaderImageBlock | null {
  if (!sanitizeRecognizedHeader(headerImage.html)) return null;

  return createPageHeaderImage(source);
}

export function splitPageContentTranslations(translations: TranslatedField): {
  headerImageTranslations: PageHeaderImageTranslations;
  headerImagePrefixTranslations: PageHeaderImagePrefixTranslations;
  contentTranslations: TranslatedField;
} {
  const headerImageTranslations: PageHeaderImageTranslations = {};
  const headerImagePrefixTranslations: PageHeaderImagePrefixTranslations = {};
  const contentTranslations: TranslatedField = {};

  for (const [locale, content] of Object.entries(translations)) {
    if (content === null) {
      headerImagePrefixTranslations[locale] = '';
      contentTranslations[locale] = null;
      continue;
    }

    const { headerImage, prefixHtml, bodyHtml } = parsePageHeaderImage(content);
    if (headerImage) headerImageTranslations[locale] = headerImage;
    headerImagePrefixTranslations[locale] = prefixHtml;
    contentTranslations[locale] = bodyHtml;
  }

  return {
    headerImageTranslations,
    headerImagePrefixTranslations,
    contentTranslations,
  };
}

export function composePageContent(
  content: string | null | undefined,
  headerImage: PageHeaderImageBlock | null | undefined = null,
  prefixHtml = ''
) {
  return headerImage || prefixHtml
    ? `${prefixHtml}${headerImage?.html ?? ''}${content ?? ''}`
    : content ?? null;
}
